import { useMediaQuery } from "@uidotdev/usehooks";
import * as React from "react";
import { XIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

export interface ResponsiveDialogProps {
  trigger?: React.ReactNode;
  children: React.ReactNode;
  title?: string;
  description?: string;
  open?: boolean;
  dismissible?: boolean;
  onOpenChange?: (open: boolean) => void;
  topBanner?: React.ReactNode;
  showCloseButton?: boolean;
  className?: string;
  force?: "dialog" | "drawer";
  fullHeight?: boolean;
  fullScreen?: boolean;
  overlayOpacity?: number;
  /** Dialog only: the element to portal into (see `DialogContent`). */
  container?: HTMLElement | null;
}

const ResponsiveDialog: React.FC<ResponsiveDialogProps> = ({
  trigger,
  children,
  title,
  description,
  open,
  onOpenChange,
  className,
  topBanner,
  dismissible = true,
  fullHeight = false,
  fullScreen = false,
  showCloseButton = true,
  force,
  overlayOpacity = 0.5,
  container,
}) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  if (force === "dialog" || (isDesktop && force !== "drawer")) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger asChild onClick={() => onOpenChange?.(true)}>
          {trigger}
        </DialogTrigger>
        <DialogContent
          opacity={overlayOpacity}
          className={cn("sm:max-w-lg", className)}
          showCloseButton={showCloseButton}
          onOpenAutoFocus={(e) => e.preventDefault()}
          fullHeight={fullHeight}
          fullScreen={fullScreen}
          container={container}
        >
          {topBanner}
          {(title || description) && (
            <div className="flex w-full flex-col items-center gap-1">
              {title && (
                <DialogHeader>
                  <DialogTitle className="text-center">{title}</DialogTitle>
                </DialogHeader>
              )}
              {description && (
                <DialogDescription className="text-center">
                  {description}
                </DialogDescription>
              )}
            </div>
          )}
          {children}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      dismissible={dismissible}
      repositionInputs={false}
    >
      <DrawerTrigger asChild onClick={() => onOpenChange?.(true)}>
        {trigger}
      </DrawerTrigger>
      <DrawerContent
        className={cn("mx-auto overflow-hidden sm:max-w-3xl", className)}
        fullHeight={fullHeight}
        fullScreen={fullScreen}
      >
        {topBanner}
        {showCloseButton && (
          <DrawerClose
            asChild
            className="absolute -top-2 right-2 -translate-y-full"
          >
            <XIcon />
          </DrawerClose>
        )}
        {(title || description) && (
          <div className="flex w-full flex-col items-center gap-1">
            {title && (
              <DrawerHeader>
                <DrawerTitle className="text-center">{title}</DrawerTitle>
              </DrawerHeader>
            )}
            {description && (
              <DrawerDescription className="text-center">
                {description}
              </DrawerDescription>
            )}
          </div>
        )}
        {children}
      </DrawerContent>
    </Drawer>
  );
};

export default ResponsiveDialog;
